'use client';

// module imports
import { DragEvent, useCallback, useRef, useState } from 'react';

// file imports
import axiosInstance from '@/lib/axios';
import { showError, showSuccess } from '@/lib/toast';

// Types
interface DragDropUploadProps {
  onUploadSuccess: (url: string, fileName: string) => void;
  onUploadError?: (error: string) => void;
  folder?: string;
  makePublic?: boolean;
  accept?: string;
  maxSize?: number; // in MB
  maxFiles?: number;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
}

interface UploadProgress {
  isUploading: boolean;
  progress: number;
  fileName?: string;
}

export default function DragDropUpload({
  onUploadSuccess,
  onUploadError,
  folder = 'uploads',
  makePublic = true,
  accept = '*/*',
  maxSize = 10, // 10MB default
  maxFiles = 1,
  className = '',
  children,
  disabled = false,
}: DragDropUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    progress: 0,
  });

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        return `File size must be less than ${maxSize}MB`;
      }

      // Check file type if accept is specified
      if (accept !== '*/*') {
        const acceptedTypes = accept.split(',').map((type) => type.trim());
        const fileType = file.type;
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

        const isAccepted = acceptedTypes.some((type) => type === fileType || type === fileExtension || (type.endsWith('/*') && fileType.startsWith(type.replace('/*', ''))));

        if (!isAccepted) {
          return `File type not supported. Accepted types: ${accept}`;
        }
      }

      return null;
    },
    [maxSize, accept]
  );

  const handleFileUpload = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        showError(validationError);
        onUploadError?.(validationError);
        return;
      }

      setUploadProgress({
        isUploading: true,
        progress: 0,
        fileName: file.name,
      });

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        formData.append('makePublic', makePublic.toString());

        const response = await axiosInstance.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress((prev) => ({ ...prev, progress }));
            }
          },
        });

        if (response.data.success) {
          showSuccess('File uploaded successfully!');
          onUploadSuccess(response.data.data.url, response.data.data.fileName);
        } else {
          throw new Error(response.data.error || 'Upload failed');
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
        showError(errorMessage);
        onUploadError?.(errorMessage);
      } finally {
        setUploadProgress({
          isUploading: false,
          progress: 0,
        });
      }
    },
    [folder, makePublic, validateFile, onUploadSuccess, onUploadError]
  );

  const handleFiles = useCallback(
    (files: FileList) => {
      const fileArray = Array.from(files);

      if (fileArray.length > maxFiles) {
        showError(`Maximum ${maxFiles} file(s) allowed`);
        return;
      }

      fileArray.forEach((file) => {
        handleFileUpload(file);
      });
    },
    [maxFiles, handleFileUpload]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files) {
        handleFiles(files);
      }
      // Reset input value
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFiles]
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !uploadProgress.isUploading) {
        setIsDragOver(true);
      }
    },
    [disabled, uploadProgress.isUploading]
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled || uploadProgress.isUploading) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFiles(files);
      }
    },
    [disabled, uploadProgress.isUploading, handleFiles]
  );

  const handleClick = useCallback(() => {
    if (!disabled && !uploadProgress.isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, uploadProgress.isUploading]);

  return (
    <div className={className}>
      <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileChange} multiple={maxFiles > 1} className="hidden" disabled={disabled || uploadProgress.isUploading} />

      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
          ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${disabled || uploadProgress.isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        {children || (
          <div className="space-y-4">
            {uploadProgress.isUploading ? (
              <div className="space-y-3">
                <div className="text-lg font-medium text-gray-700">Uploading {uploadProgress.fileName}...</div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-blue-600 h-3 rounded-full transition-all duration-300" style={{ width: `${uploadProgress.progress}%` }} />
                </div>
                <div className="text-sm text-gray-600">{uploadProgress.progress}%</div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-4xl text-gray-400">📁</div>
                <div className="text-lg font-medium text-gray-700">{isDragOver ? 'Drop files here' : 'Drag & drop files here'}</div>
                <div className="text-sm text-gray-500">
                  or <span className="text-blue-600 underline">browse files</span>
                </div>
                <div className="text-xs text-gray-400 space-y-1">
                  <div>Max size: {maxSize}MB per file</div>
                  {maxFiles > 1 && <div>Max files: {maxFiles}</div>}
                  {accept !== '*/*' && <div>Accepted: {accept}</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
